import type {
  AdConfig,
  BrandingConfig,
  ClientConfig,
  FeatureConfig,
  LanguageConfig,
  RefreshConfig,
  RuntimeConfig,
  ThemeConfig,
  VideoConfig,
} from './types'

/**
 * Fallback values used when a client config omits a field.
 * All values match the current AccuWeather app's implicit behavior exactly.
 */

export const defaultTheme: ThemeConfig = {
  backgroundColor: '#000000',
  primaryColor: '#000000',
  accentColor: '#CF0A0A',
  textColor: '#ffffff',
  surfaceColor: 'rgba(255, 255, 255, 0.05)',
}

export const defaultBranding: BrandingConfig = {
  logoUrl: null, // null = render bundled accuweather-logo.svg
  logoAlt: 'AccuWeather',
  logoMaxWidth: 334, // matches current hardcoded width in SCSS
  logoMaxHeight: 69, // matches current hardcoded height in SCSS
  backgroundImageUrl: null, // null = solid color background
}

export const defaultVideo: VideoConfig = {
  provider: 'hls',
  feedUrl: null, // null = read VUE_APP_AW_NOW_SRC at runtime
  label: 'ACCUWEATHER NOW',
  closedCaptions: true,
  fitMode: 'cover',
  fullscreenFitMode: 'cover',
  position: 'center',
  showHeaderOverlay: true,
  showLiveIndicator: true,
  defaultQualityLevel: 2, // matches current HLS hardcoded value
  windowedQualityLevel: 1, // matches current HLS hardcoded value
  fullscreenQualityLevel: 4, // matches current HLS hardcoded value
  hls: { maxBufferLength: 25 }, // matches current HLS hardcoded value
}

export const defaultAd: AdConfig = {
  enabled: true,
  imageUrl: null, // null = render bundled ADQr.svg
  alt: 'Advertisement',
  placement: 'location',
  preserveLayoutSpace: true,
}

export const defaultLanguage: LanguageConfig = {
  defaultLanguage: 'en',
  supportedLanguages: ['en'],
  apiLanguageMap: { en: 'en-us', es: 'es' },
}

export const defaultFeatures: FeatureConfig = {
  showVideo: true,
  showLogo: true,
}

export const defaultRefresh: RefreshConfig = {
  enabled: true,
  intervals: {
    currentConditionsMs: 15 * 60 * 1000,
    hourlyForecastMs: 30 * 60 * 1000,
    dailyForecastMs: 12 * 60 * 60 * 1000,
    mapsMs: 30 * 60 * 1000,
  },
}

export const defaultRuntimeConfig: RuntimeConfig = {
  enabled: false,
  url: null,
  timeoutMs: 3000,
}

/**
 * Placeholder ClientConfig used as the initial state for the Vuex `clientConfig` module
 * before the real client config is loaded (asynchronously, via dynamic import) in main.ts.
 * Matches AccuWeather's behavior so any early reads degrade gracefully.
 */
export const placeholderClientConfig: ClientConfig = {
  clientId: 'accuweather',
  displayName: 'AccuWeather NOW',
  domain: { appServiceUrl: null, customDomain: null },
  branding: defaultBranding,
  theme: defaultTheme,
  video: defaultVideo,
  ad: defaultAd,
  language: defaultLanguage,
  features: defaultFeatures,
  refresh: defaultRefresh,
  runtimeConfig: defaultRuntimeConfig,
}
